/**
 * Base HTTP client following APIClient pattern from pkg/netrunner/clients/http_client.go
 * Provides retry logic, error handling, and request/response abstraction
 */

import { RequestConfig, Response } from '../../types';
import { ServiceUnavailableError } from '../../types/api';

export class HttpClient {
  private baseURL: string;
  private timeout: number;
  private maxRetries: number;

  constructor(baseURL: string, maxRetries: number = 3, timeout: number = 30000) {
    // Remove trailing slash for consistency
    this.baseURL = baseURL.replace(/\/$/, '');
    this.maxRetries = maxRetries;
    this.timeout = timeout;
  }

  /**
   * Execute HTTP request with retry logic
   * Follows pattern from APIClient.Do() in http_client.go
   */
  async do(config: RequestConfig): Promise<Response> {
    const startTime = Date.now();
    let lastError: Error | null = null;
    let lastErrorBody: ServiceUnavailableError | null = null;

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        const response = await this.doRequest(config);
        return {
          ...response,
          duration: Date.now() - startTime,
        };
      } catch (error) {
        lastError = error as Error;
        
        // Don't retry nonce errors - let higher-level logic handle them with new nonces
        if (this.isNonceError(error)) {
          throw error;
        }

        // Parse error body to check for service unavailability details
        const errorBody = this.parseErrorBody(error);
        lastErrorBody = errorBody;

        // Check if circuit breaker is OPEN - fail immediately, no retries
        if (this.isServiceUnavailable(errorBody)) {
          const retryAfter = errorBody?.details?.retry_after_seconds;
          const circuitBreakerState = errorBody?.details?.circuit_breaker_state;
          const readsAllowed = errorBody?.details?.reads_allowed;
          const requestType = errorBody?.details?.request_type;
          
          // If circuit breaker is OPEN, fail immediately with clear message
          // Don't retry when circuit breaker is OPEN - service is protecting itself
          if (circuitBreakerState === 'OPEN') {
            const serviceStatus = errorBody?.details?.service_status || 'UNAVAILABLE';
            const allowedOps = [];
            if (readsAllowed) allowedOps.push('reads');
            if (errorBody?.details?.writes_allowed) allowedOps.push('writes');
            if (errorBody?.details?.streams_allowed) allowedOps.push('streams');
            
            const allowedOpsStr = allowedOps.length > 0 
              ? ` (Allowed: ${allowedOps.join(', ')})` 
              : ' (No operations allowed)';
            
            throw new Error(
              `Service temporarily unavailable: Circuit breaker is OPEN. ` +
              `Service status: ${serviceStatus}.${allowedOpsStr} ` +
              `Retry after ${retryAfter || 30} seconds. ` +
              `Request type: ${requestType || 'unknown'}. ` +
              `Details: ${errorBody?.message || 'Service unavailable'}`
            );
          }

          // If retry_after_seconds is provided and circuit breaker is not OPEN, use it for backoff
          if (retryAfter && attempt < this.maxRetries) {
            await this.delay(retryAfter * 1000);
            continue;
          }
        }

        // Exponential backoff before retry (fallback if no retry_after_seconds)
        if (attempt < this.maxRetries) {
          const backoff = attempt * 1000; // 1s, 2s, 3s...
          await this.delay(backoff);
        }
      }
    }

    // Build a more informative error message
    let errorMessage = `Request failed after ${this.maxRetries} attempts: ${lastError?.message}`;
    if (lastErrorBody?.details?.retry_after_seconds) {
      errorMessage += ` (Service requested retry after ${lastErrorBody.details.retry_after_seconds} seconds)`;
    }
    if (lastErrorBody?.details?.circuit_breaker_state) {
      errorMessage += ` (Circuit breaker: ${lastErrorBody.details.circuit_breaker_state})`;
    }

    throw new Error(errorMessage);
  }

  /**
   * Perform a single HTTP request
   * Follows pattern from APIClient.doRequest() in http_client.go
   */
  private async doRequest(config: RequestConfig): Promise<Omit<Response, 'duration'>> {
    // Construct full URL
    const url = `${this.baseURL}${config.path}`;
    
    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json, */*',
      ...config.headers,
    };

    // Prepare request options
    const options: RequestInit = {
      method: config.method,
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };

    // Add body for non-GET requests
    if (config.body && config.method !== 'GET') {
      options.body = JSON.stringify(config.body);
    } else if (config.method === 'GET') {
      // Force nil body for GET requests (matches Go client behavior)
      options.body = undefined;
    }

    // Execute request
    const response = await fetch(url, options);
    
    // Parse response body
    let body: any;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      try {
        body = await response.json();
      } catch {
        body = await response.text();
      }
    } else {
      body = await response.text();
    }

    // Check for HTTP errors
    if (response.status >= 400) {
      throw new Error(`HTTP ${response.status}: ${JSON.stringify(body)}`);
    }

    return {
      statusCode: response.status,
      body,
      headers: response.headers,
    };
  }

  /**
   * Check if error is a nonce error (should not retry)
   * Follows pattern from APIClient.Do() nonce error detection
   */
  private isNonceError(error: any): boolean {
    const message = error?.message || '';
    return message.toLowerCase().includes('nonce') || 
           message.includes('InvalidNonce') ||
           message.includes('NonceError');
  }

  /**
   * Parse error body from error message
   * Error messages are in format: "HTTP {status}: {JSON.stringify(body)}"
   */
  private parseErrorBody(error: any): ServiceUnavailableError | null {
    try {
      const message = error?.message || '';
      // Extract JSON from error message (format: "HTTP 503: {...}")
      const jsonMatch = message.match(/HTTP \d+: (.+)$/);
      if (jsonMatch && jsonMatch[1]) {
        const parsed = JSON.parse(jsonMatch[1]);
        // Type guard: check if it matches ServiceUnavailableError structure
        if (parsed && typeof parsed === 'object' && (parsed.error === 503 || parsed.code === 'SERVICE_UNAVAILABLE')) {
          return parsed as ServiceUnavailableError;
        }
      }
    } catch {
      // If parsing fails, return null
    }
    return null;
  }

  /**
   * Check if error indicates service unavailability (503 with circuit breaker)
   */
  private isServiceUnavailable(errorBody: ServiceUnavailableError | null): boolean {
    if (!errorBody) return false;
    return errorBody.error === 503 || 
           errorBody.code === 'SERVICE_UNAVAILABLE' ||
           errorBody.details?.circuit_breaker_state === 'OPEN';
  }

  /**
   * Delay helper for exponential backoff
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * GET request convenience method
   */
  async get(path: string, headers?: Record<string, string>): Promise<Response> {
    return this.do({ method: 'GET', path, headers });
  }

  /**
   * POST request convenience method
   */
  async post(path: string, body: any, headers?: Record<string, string>): Promise<Response> {
    return this.do({ method: 'POST', path, body, headers });
  }

  /**
   * PUT request convenience method
   */
  async put(path: string, body: any, headers?: Record<string, string>): Promise<Response> {
    return this.do({ method: 'PUT', path, body, headers });
  }

  /**
   * DELETE request convenience method
   */
  async delete(path: string, headers?: Record<string, string>): Promise<Response> {
    return this.do({ method: 'DELETE', path, headers });
  }

  /**
   * Get base URL
   */
  getBaseURL(): string {
    return this.baseURL;
  }

  /**
   * Set timeout
   */
  setTimeout(timeout: number): void {
    this.timeout = timeout;
  }

  /**
   * Get timeout
   */
  getTimeout(): number {
    return this.timeout;
  }
}
